// ─────────────────────────────────────────────────────────────────────────────
// Dynamic Deposit Configuration
// -----------------------------------------------------------------------------
// Everything in the deposit flow that used to be hardcoded per payment type
// (USDT vs BDT, CashOut/Merchant/Send Money channels, currency symbol, amount
// range & presets) is now driven from config maps keyed by the payment
// system's `account_type`, with `name` / `currency` / `network` fallbacks.
//
// To add support for ANY new payment type, just add an entry here — the
// Deposit Channel section, currency symbol, amount range, presets and the
// crypto/BDT detection all adapt automatically. No if/else chains needed.
// ─────────────────────────────────────────────────────────────────────────────

// ---- Deposit Channel display labels (account_type → label) ----
export const DEPOSIT_CHANNEL_LABELS = {
  // BDT / mobile-banking channels
  Agent: 'CashOut',
  Merchant: 'Merchant',
  Marchent: 'Merchant',
  Personal: 'Send Money',
  // Crypto channels
  USDT: 'USDT',
  Bitcoin: 'Bitcoin',
  Ethereum: 'Ethereum',
  TRON: 'TRON',
  'TRON USDT (TRC20)': 'USDT (TRC20)',
  'Ethereum USDT (ERC20)': 'USDT (ERC20)',
  'BNB Smart Chain USDT (BEP20)': 'USDT (BEP20)',
};

// Pretty-print a raw account_type (e.g. "bn_b_chain" → "Bn B Chain")
const pretty = (s = '') =>
  String(s)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Label shown for a deposit channel. Falls back to network → account_type → name,
// so ANY new account_type still gets a sensible label.
export const getDepositChannelLabel = (ps = {}) => {
  const type = ps.account_type;
  if (DEPOSIT_CHANNEL_LABELS[type]) return DEPOSIT_CHANNEL_LABELS[type];
  if (ps.network) return ps.network;
  if (type) return pretty(type);
  if (ps.name) return ps.name;
  return null;
};

// ---- Crypto detection ----
export const CRYPTO_ACCOUNT_TYPES = new Set([
  'USDT',
  'Bitcoin',
  'Ethereum',
  'TRON',
  'TRON USDT (TRC20)',
  'Ethereum USDT (ERC20)',
  'BNB Smart Chain USDT (BEP20)',
]);

const CRYPTO_CURRENCIES = new Set(['btc', 'eth', 'trx', 'usdt', 'usdttrc20', 'usdterc20', 'usdtbsc']);

const CRYPTO_RE = /crypto|bitcoin|ethereum|tron|usdt|btc|eth|trx|trc20|erc20|bep20/i;

// Used where we only have the account_type string (e.g. MakePayment page).
export const isCryptoAccountType = (type = '') =>
  CRYPTO_ACCOUNT_TYPES.has(type) || CRYPTO_RE.test(String(type));

// Used where we have the full payment-system object. A channel counts as crypto
// when it carries crypto-specific fields (provider / network / currency) or has
// a known crypto account_type.
export const isCryptoChannel = (ps = {}) =>
  CRYPTO_ACCOUNT_TYPES.has(ps.account_type) ||
  Boolean(ps.provider) ||
  Boolean(ps.network) ||
  CRYPTO_CURRENCIES.has(String(ps.currency).toLowerCase());

// ---- Shared presets ----
const BDT_PRESETS = ['100', '500', '1000', '2000', '3000', '5000', '10000', '15000', '20000', '25000'];
const USDT_PRESETS = ['10', '20', '30', '50', '100', '150', '200'];
const BTC_PRESETS = ['0.0001', '0.0005', '0.001', '0.002', '0.005', '0.01'];
const ETH_PRESETS = ['0.01', '0.05', '0.1', '0.2', '0.5', '1'];
const TRX_PRESETS = ['50', '100', '500', '1000', '2000', '5000'];

// ---- Per account_type deposit UI config ----
// symbol      : currency symbol shown in the amount section
// range       : [min, max] displayed next to "Amount"
// presets     : quick-amount buttons
// showRate    : show the "1 X = BDT/TK" conversion line (uses usdtRate from settings)
// rateLabel   : the asset name used in that conversion line
// hideChannel : true → hide the Deposit Channel selector (single auto-selected channel)
// recommended : true → show the "recommended" tag in the channel grid
export const DEPOSIT_CONFIGS = {
  // BDT / mobile-banking
  Agent: { symbol: '৳', range: [100, 25000], presets: BDT_PRESETS, showRate: false, hideChannel: false, recommended: true },
  Merchant: { symbol: '৳', range: [100, 25000], presets: BDT_PRESETS, showRate: false, hideChannel: false, recommended: true },
  Marchent: { symbol: '৳', range: [100, 25000], presets: BDT_PRESETS, showRate: false, hideChannel: false, recommended: true },
  Personal: { symbol: '৳', range: [100, 25000], presets: BDT_PRESETS, showRate: false, hideChannel: false, recommended: false },

  // Legacy single USDT channel — keep the channel selector hidden as before
  USDT: { symbol: '$', range: [10, 200], presets: USDT_PRESETS, showRate: true, rateLabel: 'USDT', hideChannel: true, recommended: false, isCrypto: true },

  // Crypto networks (each is its own selectable Deposit Channel)
  'TRON USDT (TRC20)': { symbol: '$', range: [10, 200], presets: USDT_PRESETS, showRate: true, rateLabel: 'USDT', hideChannel: false, recommended: false, isCrypto: true },
  'Ethereum USDT (ERC20)': { symbol: '$', range: [10, 200], presets: USDT_PRESETS, showRate: true, rateLabel: 'USDT', hideChannel: false, recommended: false, isCrypto: true },
  'BNB Smart Chain USDT (BEP20)': { symbol: '$', range: [10, 200], presets: USDT_PRESETS, showRate: true, rateLabel: 'USDT', hideChannel: false, recommended: false, isCrypto: true },
  Bitcoin: { symbol: '₿', range: [0.0001, 0.05], presets: BTC_PRESETS, showRate: false, hideChannel: false, recommended: false, isCrypto: true },
  Ethereum: { symbol: 'Ξ', range: [0.01, 2], presets: ETH_PRESETS, showRate: false, hideChannel: false, recommended: false, isCrypto: true },
  TRON: { symbol: '₮', range: [50, 5000], presets: TRX_PRESETS, showRate: false, hideChannel: false, recommended: false, isCrypto: true },
};

// Fallbacks used when a brand-new account_type isn't in DEPOSIT_CONFIGS yet,
// so "any and all" types still behave sensibly.
const NAME_CONFIGS = {
  Crypto: { symbol: '$', range: [10, 200], presets: USDT_PRESETS, showRate: true, rateLabel: 'USDT', hideChannel: false, recommended: false, isCrypto: true },
};

const CURRENCY_CONFIGS = {
  usdt: { symbol: '$', range: [10, 200], presets: USDT_PRESETS, showRate: true, rateLabel: 'USDT', hideChannel: false, recommended: false, isCrypto: true },
  btc: { symbol: '₿', range: [0.0001, 0.05], presets: BTC_PRESETS, showRate: false, hideChannel: false, recommended: false, isCrypto: true },
  eth: { symbol: 'Ξ', range: [0.01, 2], presets: ETH_PRESETS, showRate: false, hideChannel: false, recommended: false, isCrypto: true },
  trx: { symbol: '₮', range: [50, 5000], presets: TRX_PRESETS, showRate: false, hideChannel: false, recommended: false, isCrypto: true },
};

const BDT_DEFAULT = { symbol: '৳', range: [100, 25000], presets: BDT_PRESETS, showRate: false, hideChannel: false, recommended: false };

// Resolve the deposit UI config for any payment-system object.
export const getDepositConfig = (ps = {}) => {
  if (!ps) return BDT_DEFAULT;
  if (DEPOSIT_CONFIGS[ps.account_type]) return DEPOSIT_CONFIGS[ps.account_type];
  if (ps.name && NAME_CONFIGS[ps.name]) return NAME_CONFIGS[ps.name];
  if (ps.currency && CURRENCY_CONFIGS[String(ps.currency).toLowerCase()]) {
    return CURRENCY_CONFIGS[String(ps.currency).toLowerCase()];
  }
  if (isCryptoChannel(ps)) return NAME_CONFIGS.Crypto;
  return BDT_DEFAULT;
};
